/**
 * ViewSettingsSheet encapsulates the sheet/accordion UI used to toggle
 * view preferences (navigation widgets, client tabs, discovery tabs, dev tools).
 * It takes the current settings + update callbacks from ViewSettingsContext.
 */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { FEATURE_FLAGS } from '@/config/feature-flags';
import { COMPONENT_GAP } from '@/design-system/constants/spacing';
import { useViewSettings } from '../../contexts/ViewSettingsContext';

type ViewSettingsState = ReturnType<typeof useViewSettings>['viewSettings'];
type UpdateViewSettingFn = ReturnType<typeof useViewSettings>['updateViewSetting'];
type UpdateClientDetailsTabSettingFn = ReturnType<
  typeof useViewSettings
>['updateClientDetailsTabSetting'];
type UpdateClientDiscoveryTabSettingFn = ReturnType<
  typeof useViewSettings
>['updateClientDiscoveryTabSetting'];
type UpdateCorporateDashboardTabSettingFn = ReturnType<
  typeof useViewSettings
>['updateCorporateDashboardTabSetting'];
type ResetViewSettingsFn = ReturnType<typeof useViewSettings>['resetViewSettings'];

type ViewSettingsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewSettings: ViewSettingsState;
  updateViewSetting: UpdateViewSettingFn;
  updateClientDetailsTabSetting: UpdateClientDetailsTabSettingFn;
  updateClientDiscoveryTabSetting: UpdateClientDiscoveryTabSettingFn;
  updateCorporateDashboardTabSetting: UpdateCorporateDashboardTabSettingFn;
  resetViewSettings: ResetViewSettingsFn;
  flags: Record<string, boolean>;
};

export const ViewSettingsSheet = ({
  open,
  onOpenChange,
  viewSettings,
  updateViewSetting,
  updateClientDetailsTabSetting,
  updateClientDiscoveryTabSetting,
  updateCorporateDashboardTabSetting,
  resetViewSettings,
  flags,
}: ViewSettingsSheetProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent
      side="right"
      className="w-full sm:w-[400px] overflow-y-auto bg-white rounded-l-3xl shadow-2xl border-l border-gray-100"
      data-testid="viewSettings_sheet"
      style={{
        top: `${COMPONENT_GAP}px`,
        height: `calc(100vh - ${COMPONENT_GAP * 3}px)`,
        right: `${COMPONENT_GAP}px`,
      }}
    >
      <SheetHeader className="pb-6">
        <SheetTitle>Display Preferences</SheetTitle>
        <SheetDescription>Customize what you see across the application</SheetDescription>
      </SheetHeader>

      <div className="space-y-6">
        <div className="space-y-4 pb-2">
          <h3 className="text-sm font-medium">Navigation</h3>
          <div className="flex items-center justify-between">
            <Label htmlFor="show-analytics" className="text-sm font-normal cursor-pointer">
              Analytics
            </Label>
            <Switch
              id="show-analytics"
              checked={viewSettings.showAnalytics}
              onCheckedChange={(checked) => updateViewSetting('showAnalytics', checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label
              htmlFor="show-corporate-management"
              className="text-sm font-normal cursor-pointer"
            >
              Corporate Management
            </Label>
            <Switch
              id="show-corporate-management"
              checked={viewSettings.showCorporateManagement}
              onCheckedChange={(checked) => updateViewSetting('showCorporateManagement', checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="show-builder" className="text-sm font-normal cursor-pointer">
              Builder
            </Label>
            <Switch
              id="show-builder"
              checked={viewSettings.showBuilder}
              onCheckedChange={(checked) => updateViewSetting('showBuilder', checked)}
            />
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full" defaultValue="hub">
          <AccordionItem value="hub" className="border-b">
            <AccordionTrigger className="hover:no-underline">
              <span className="text-sm font-medium">Hub Page</span>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              <div className="space-y-4">
                {[
                  { key: 'hubAppointments', label: 'Appointments' },
                  { key: 'hubClients', label: 'Users' },
                  { key: 'hubTickets', label: 'Tickets' },
                ].map((section) => (
                  <div key={section.key} className="flex items-center justify-between">
                    <Label
                      htmlFor={`hub-${section.key}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {section.label}
                    </Label>
                    <Switch
                      id={`hub-${section.key}`}
                      checked={viewSettings[section.key as keyof ViewSettingsState]}
                      onCheckedChange={(checked) =>
                        updateViewSetting(
                          section.key as keyof ViewSettingsState,
                          checked as boolean
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="client-details" className="border-b">
            <AccordionTrigger className="hover:no-underline">
              <span className="text-sm font-medium">Client Details Page</span>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              <div className="space-y-4">
                {[
                  { key: 'visits', label: 'Visits' },
                  { key: 'journey', label: 'Journey' },
                  { key: 'consultations', label: 'Consultations' },
                  { key: 'context', label: 'Context' },
                  { key: 'prep', label: 'Prep' },
                  { key: 'biomarker_analysis', label: 'Biomarker Analysis' },
                  { key: 'reports', label: 'Reports' },
                  { key: 'fitness_assessment', label: 'Fitness Assessment' },
                  { key: 'offerings', label: 'Programs' },
                  { key: 'test_enrollments', label: 'Tests' },
                  { key: 'clinical_notes', label: 'Clinical Notes' },
                  { key: 'internal_notes', label: 'Internal Notes' },
                  { key: 'journal', label: 'Nutrition' },
                  { key: 'daily_log', label: 'Daily Log' },
                  { key: 'appointments', label: 'Appointments' },
                  { key: 'clinical', label: 'Clinical' },
                  { key: 'care_team', label: 'Care Team' },
                  { key: 'marketplace', label: 'Marketplace' },
                  { key: 'questionnaires', label: 'Questionnaires' },
                  { key: 'videos', label: 'Videos' },
                  { key: 'pmx_reports', label: 'PMX Reports' },
                  { key: 'thrive_intelligence', label: 'Thrive Intelligence' },
                  { key: 'workbench', label: 'Workbench' },
                  { key: 'genetic_features', label: 'Genetic Features' },
                  { key: 'roadmap', label: 'Roadmap' },
                  { key: 'reminders', label: 'Reminders' },
                  { key: 'wearables', label: 'Wearables' },
                  { key: 'wearable_analysis', label: 'Wearable Analysis' },
                  ...(flags[FEATURE_FLAGS.ENABLE_SIFT] ? [{ key: 'sift', label: 'SIFT' }] : []),
                ].map((tab) => (
                  <div key={tab.key} className="flex items-center justify-between">
                    <Label htmlFor={`cd-${tab.key}`} className="text-sm font-normal cursor-pointer">
                      {tab.label}
                    </Label>
                    <Switch
                      id={`cd-${tab.key}`}
                      checked={
                        viewSettings.clientDetailsTabs?.[
                          tab.key as keyof typeof viewSettings.clientDetailsTabs
                        ] ?? true
                      }
                      onCheckedChange={(checked) =>
                        updateClientDetailsTabSetting(
                          tab.key as keyof typeof viewSettings.clientDetailsTabs,
                          checked
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="corporate-dashboard" className="border-b">
            <AccordionTrigger className="hover:no-underline">
              <span className="text-sm font-medium">Corporate Dashboard</span>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              <div className="space-y-4">
                {[
                  { key: 'journey', label: 'Journey Status' },
                  { key: 'employees', label: 'Employees' },
                  { key: 'analytics', label: 'Analytics' },
                ].map((tab) => (
                  <div key={tab.key} className="flex items-center justify-between">
                    <Label
                      htmlFor={`corp-${tab.key}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {tab.label}
                    </Label>
                    <Switch
                      id={`corp-${tab.key}`}
                      checked={
                        viewSettings.corporateDashboardTabs?.[
                          tab.key as keyof typeof viewSettings.corporateDashboardTabs
                        ] ?? true
                      }
                      onCheckedChange={(checked) =>
                        updateCorporateDashboardTabSetting(
                          tab.key as keyof typeof viewSettings.corporateDashboardTabs,
                          checked
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="client-discovery" className="border-b">
            <AccordionTrigger className="hover:no-underline">
              <span className="text-sm font-medium">Client Discovery Visit Page</span>
            </AccordionTrigger>
            <AccordionContent className="pt-4">
              <div className="space-y-4">
                {[
                  { key: 'client_data', label: 'Visit Data' },
                  { key: 'precheck', label: 'Precheck' },
                  { key: 'questionnaires', label: 'Questionnaires' },
                  { key: 'assessments', label: 'Assessments' },
                  { key: 'consultation', label: "Doctor's Consultation" },
                  { key: 'external_reports', label: 'Reports' },
                  { key: 'internal_reports', label: 'PMX Reports' },
                  { key: 'biomarker_analysis', label: 'Biomarker Analysis' },
                  { key: 'roadmap', label: 'Roadmap' },
                ].map((tab) => (
                  <div key={tab.key} className="flex items-center justify-between">
                    <Label
                      htmlFor={`cdv-${tab.key}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {tab.label}
                    </Label>
                    <Switch
                      id={`cdv-${tab.key}`}
                      checked={
                        viewSettings.clientDiscoveryTabs?.[
                          tab.key as keyof typeof viewSettings.clientDiscoveryTabs
                        ] ?? true
                      }
                      onCheckedChange={(checked) =>
                        updateClientDiscoveryTabSetting(
                          tab.key as keyof typeof viewSettings.clientDiscoveryTabs,
                          checked
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="space-y-4 pt-6">
          <h3 className="text-sm font-medium">Developer Tools</h3>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="debug-panel" className="text-sm font-normal cursor-pointer">
                Debug Panel
              </Label>
              <p className="text-xs text-gray-500">Track API calls and errors (Ctrl+Shift+D)</p>
            </div>
            <Switch
              id="debug-panel"
              checked={viewSettings.debugPanel}
              onCheckedChange={(checked) => updateViewSetting('debugPanel', checked)}
            />
          </div>
        </div>

        <div className="pt-6">
          <Button variant="outline" className="w-full" onClick={resetViewSettings}>
            Reset to Defaults
          </Button>
        </div>
      </div>
    </SheetContent>
  </Sheet>
);
